import Styles from './Table.scss';

import Template from './Table.hbs';
import InputUtil from 'util/InputUtil'
import Util from 'util/util'
import {generateTemplate, getCell, getRectangularTableData} from 'views/components/taskGroups/tasks/tableTemplate/TableUtils'

export default class Template22Table extends BaseView {

    get events() {
        return {
            'beforeinput td .js-input[contenteditable="true"]': 'onBeforeInputGap',
            'keydown td .js-input[contenteditable="true"]': 'onKeyDownGap',
        }
    }

    get gapCells() {
        return this.tableData.filter((cell) => cell.isGapCell)
    }

    initialize({
        tableData = [],
        columnWidths = [],
        response,
        isReadOnly
    }) {

        this.tableData = getRectangularTableData(tableData)
        this.response = response

        this.setElement(generateTemplate(Template, Styles, this.tableData, columnWidths))

        this.setupTable(isReadOnly)

        // This fixes a Firefox bug where it inserts a <br> in contenteditables, which prevents :empty from working
        // See https://bugzilla.mozilla.org/show_bug.cgi?id=1615852
        if (window.uaparser.getBrowser().name === 'Firefox') {
            setTimeout(() => {
                for (const cell of this.el.querySelectorAll('td .js-input[contenteditable=true]')) {
                    if (cell.innerText === '\n') {
                        cell.innerText = ''
                    }
                }
            }, 0)
        }

    }

    /**
     * Applies content and properties to the table element.
     *
     * @param {Boolean} isReadOnly
     * If true, disable the contenteditable of all table cell elements.
     */
    setupTable(isReadOnly) {

        for (const cell of this.tableData) {

            if (cell.isGapCell) {
                this.setupCellGapElement(cell, isReadOnly)
            }

            this.applyCellElementStyling(cell)

            this.setCellElementContent(cell)

        }
    }

    /**
     * For a given cell coordinate, get the table cell element or the inner input element
     *
     * @param {Number} rowIndex row
     * @param {Number} columnIndex column
     * @param {Boolean} isGapCell   is cell is gap, get inner input element instead
     * @returns {HTMLTableCellElement|HTMLSpanElement} table cell element or input span element
     */
    getCellElement(rowIndex, columnIndex, isGapCell) {
        if (isGapCell) {
            return this.el.querySelector(`table tr[data-row="${rowIndex}"] td[data-column="${columnIndex}"] .js-input`)
        }
        return this.el.querySelector(`table tr[data-row="${rowIndex}"] td[data-column="${columnIndex}"]`)
    }

    isCellElement(element) {
        return element.matches('TABLE TD') || this.isGapCellElement(element)
    }

    isGapCellElement(element) {
        return element.matches('TABLE TD .js-input')
    }

    /**
     * Get sanitized gap cell text content from a student response (if present) or the table data itself
     *
     * @param {Number} rowIndex     row
     * @param {Number} columnIndex  column
     * @param {Boolean} showAnswer  show correct answer for this gap cell
     * @returns {String} gap cell text
     */
    getGapText(rowIndex, columnIndex, showAnswer) {
        if (showAnswer) {
            const cell = getCell(this.tableData, rowIndex, columnIndex)
            if (cell.isGapCell && cell.cellText) {
                return Util.stripTags(Util.normalizeCharacters(cell.cellText))
            }
        }
        if (this.response) {
            // Allow loose comparision since the student response indexes may be saved as
            // strings instead of numbers.
            // eslint-disable-next-line eqeqeq
            const response = this.response.find(c => c.row == rowIndex && c.column == columnIndex) || {}
            if (response.text) {
                return Util.stripTags(Util.normalizeCharacters(response.text))
            }
        }
        return ''
    }

    /**
     * Sets a cell element for a given cell to be editable or not if it's a gap
     *
     * @param {Object} cell cell data
     * @param {Boolean} isReadOnly  can user edit inputs
     */
    setupCellGapElement(cell, isReadOnly) {
        const cellElement = this.getCellElement(cell.rowIndex, cell.columnIndex, true)
        cellElement.contentEditable = cell.isGapCell && !isReadOnly ? 'true' : 'false'
    }

    /**
     * Apply styling to the cell element based on the formattings array of corresponding cell data
     *
     * @param {Object} cell cell data
     */
    applyCellElementStyling(cell) {

        if (cell.isGapCell || !Array.isArray(cell.formattings)) {
            return
        }

        const cellElement = this.getCellElement(cell.rowIndex, cell.columnIndex)

        // Remove existing classes and apply classes from formattings array
        cellElement.classList.remove(...cellElement.classList)
        for (const formatting of cell.formattings) {
            cellElement.classList.add(Styles[formatting])
        }

        if (cell.formattings.includes('is-merged')) {
            const previousCellElementInRow = this.getCellElement(cell.rowIndex, cell.columnIndex - 1)
            if (previousCellElementInRow) {
                previousCellElementInRow.colSpan = 2
            }
        }

    }

    /**
     * Set cell element content based on its corresponding cell data object.
     * Remove formatting depending on if it is a gap or not.
     *
     * @param {Object} cell cell data
     * @param {Boolean} showAnswer  show correct answer for this gap cell
     */
    setCellElementContent(cell, showAnswer) {

        const cellElement = this.getCellElement(cell.rowIndex, cell.columnIndex, cell.isGapCell)

        if (cell.isGapCell) {
            cellElement.innerText = this.getGapText(cell.rowIndex, cell.columnIndex, showAnswer)
        } else if (cell.cellText) {
            cellElement.innerHTML = Util.renderContentSafely(cell.cellText)
        }

    }

    onBeforeInputGap({originalEvent: e}) {
        if (e.inputType === 'insertFromPaste' && e.dataTransfer) {
            e.preventDefault()
            document.execCommand(
                'insertText', false,
                Util.normalizeCharacters(e.dataTransfer.getData('text/plain'), {removeNewLines: true})
            )
        }
    }

    onKeyDownGap(e) {
        const cellInput = e.currentTarget
        const cellElement = cellInput.closest('TD')
        const cellRow = cellElement.closest('TR')
        let nextCellElement

        switch (e.key) {
            case 'Enter':
                this.stopAllEvents(e)
                // If there is a row below that of e.currentTarget and there is a editable cell in the same column as
                // cellElement, focus on that cell when the user hits the Enter key.
                nextCellElement = cellRow.nextElementSibling?.cells[cellElement.cellIndex]
                break
            case 'ArrowLeft':
                if (!cellInput.hasChildNodes()) {
                    nextCellElement = cellRow.cells[cellElement.cellIndex - 1]
                }
                break
            case 'ArrowRight':
                if (!cellInput.hasChildNodes()) {
                    nextCellElement = cellRow.cells[cellElement.cellIndex + 1]
                }
                break
            case 'ArrowUp':
                nextCellElement = cellRow.previousElementSibling?.cells[cellElement.cellIndex]
                break
            case 'ArrowDown':
                nextCellElement = cellRow.nextElementSibling?.cells[cellElement.cellIndex]
                break
        }

        if (!nextCellElement) {
            return
        }

        const nextCellInput = nextCellElement.querySelector('.js-input') ?? nextCellElement
        if (nextCellInput.isContentEditable) {
            nextCellInput.focus()
            InputUtil.placeCaret(nextCellInput)
        }
    }

}
